// The caster select of Godsmode, as a dialog, for a match against the AI and
// for a room. The first row chooses the opponent: Play, Invite, or Join, see
// `OpponentRow.ts`. Then the eight casters as one line of symbols, then a pane
// that tells one caster in short, see `CasterPane.ts`, then one key at the
// foot. Rob, 2026-10-12: "Now you pick TWO casters at the start of a match,
// and you have both their spells to work with".
// With a mouse, the pointer on a caster fills the pane, and a click picks the
// caster or puts it back. On a touch screen a tap fills the pane, and the
// button of the pane picks. A pick fills the circle in the corner of its key
// with a check. The order of the picks still has an effect, and the screen
// does not show it: a scroll fills the slots of the first caster first. With
// two picked, the other casters step back.
// In a room the picks are the room's. A caster is for one seat only, first
// come. Garrett, 2026-10-13: "exclusive casters". The picks of the other seat
// step back with a gray check. The key at the foot is Ready, and the match
// starts when both seats press it. Until 2026-10-14 a room had a dialog of its
// own, the lobby, and a friend was only in the menu. Garrett: "design a flow
// for creating a multiplayer match right from that first wizard screen".
// The whole sheet is in view, with no scroll, at 375 by 667 and up. Garrett,
// 2026-10-14: "I dont want it to scroll, I want it all to be visible in the
// same screen without a scrolling pane". The sheet is quiet: the casters lie
// on it with no box, and one key is filled. Garrett, on a busier draft: "its
// all so visually busy/too much...".
// The select shows after the cover closes over the board, and the board opens
// again after the select goes, see `state/boardCover.ts`.
// Close, a click outside, or Escape keeps the match that is on the board. The
// select of the first match of a page has no match to keep, and a room has no
// match to go back to. Then it has no cross and does not close, and its
// backdrop is the page color.

import { CASTERS, CASTERS_PER_SIDE, type Caster, type CasterId } from '../content/casters';
import { el } from '../lib/dom';
import { otherSeat } from '../room/protocol';
import { boardCoverStore } from '../state/boardCover';
import { isMatchWaiting, matchWaitStore } from '../state/matchWait';
import { closeCasterSelect, pickCasters } from '../state/newMatch';
import { currentOpponent, joinTypedCode, opponentStore, typedCode } from '../state/opponent';
import { focusRoomCaster, pickRoomCaster, pickRoomRandom, setRoomReady } from '../state/room';
import { roomStore } from '../state/roomStore';
import { casterSelectStore, isSettingUp } from '../state/setup';
import { createCasterChoice } from './CasterChoice';
import { createCasterPane } from './CasterPane';
import { createCheckBox } from './CheckBox';
import { createCloseButton } from './CloseButton';
import { createKeyIcon } from './keyIcons';
import { createOpponentRow } from './OpponentRow';

/** Plain words, not final copy. */
const TITLE = 'Pick two casters';
const WATCH_TITLE = 'You are watching';
const RANDOM_LABEL = 'Random';
const START_LABEL = 'Start';
const READY_LABEL = 'Ready';
const JOIN_LABEL = 'Join';
const countLabel = (picked: number): string => `(${picked}/${CASTERS_PER_SIDE})`;

const ENTRY_CLASS = 'modal--entry';
const TOLD_CLASS = 'caster-select__caster--told';
const OUT_CLASS = 'caster-select__caster--out';
const THEIRS_CLASS = 'caster-select__caster--theirs';
const THEIR_FOCUS_CLASS = 'caster-select__caster--their-focus';
const MINE_FLAG_CLASS = 'caster-select__flag--on';
const THEIR_FLAG_CLASS = 'caster-select__flag--theirs';

/** The picks the select shows: this screen's, the other seat's, and what the other seat reads. */
type Picks = {
  mine: readonly CasterId[];
  theirs: readonly CasterId[];
  theirFocus: CasterId | null;
};

export const createCasterSelect = (): HTMLDialogElement => {
  // The casters picked on this screen for a match against the AI, in the order of the picks.
  let picked: CasterId[] = [];
  // The caster the pane tells.
  let told: Caster | undefined = CASTERS[0];

  const roomPicks = (): Picks | null => {
    const room = roomStore.get();
    const view = room?.view;
    if (!room || !view) return null;
    if (!room.seat) return { mine: [], theirs: [], theirFocus: null };
    const friend = otherSeat(room.seat);
    return {
      mine: view.picks[room.seat],
      theirs: view.picks[friend],
      theirFocus: view.focus[friend],
    };
  };
  const picksNow = (): Picks => roomPicks() ?? { mine: picked, theirs: [], theirFocus: null };
  const watching = (): boolean => {
    const room = roomStore.get();
    return room !== null && room.view !== null && room.seat === null;
  };

  const pane = createCasterPane();
  const opponent = createOpponentRow(() => picksNow().mine);
  const title = el('h2', { className: 'caster-select__title' });
  const footKey = el('button', { className: 'caster-select__start', type: 'button' });

  // A pick goes in, and a second one on the same caster puts it back. With the full number
  // picked, no other caster goes in until one is put back. A room checks the picks itself.
  const toggle = (id: CasterId): void => {
    if (roomStore.get()) {
      pickRoomCaster(id);
      return;
    }
    if (picked.includes(id)) picked = picked.filter((other) => other !== id);
    else if (picked.length < CASTERS_PER_SIDE) picked = [...picked, id];
    draw();
  };

  // Random fills the picks and starts nothing, so the player can read the two casters first.
  // Garrett, 2026-10-13: "random should just select, not start". The draw is from
  // `Math.random`: the match record keeps the casters. A room draws on its server.
  const pickAtRandom = (): void => {
    if (roomStore.get()) {
      pickRoomRandom();
      return;
    }
    const pool = CASTERS.map((caster) => caster.id);
    picked = Array.from({ length: CASTERS_PER_SIDE }, () => {
      const [id] = pool.splice(Math.floor(Math.random() * pool.length), 1);
      return id;
    }).filter((id): id is CasterId => id !== undefined);
    told = CASTERS.find((caster) => caster.id === picked[0]) ?? told;
    draw();
  };

  /** The pane tells a caster. In a room the other seat sees which one. */
  const tell = (caster: Caster): void => {
    told = caster;
    if (roomStore.get()) focusRoomCaster(caster.id);
    draw();
  };

  const buttons = CASTERS.map((caster) =>
    createCasterChoice(caster, {
      tell,
      toggle: (next) => {
        tell(next);
        toggle(next.id);
      },
      told: () => told,
    }),
  );

  const randomKey = el(
    'button',
    {
      className: 'caster-select__random',
      type: 'button',
      ariaLabel: RANDOM_LABEL,
      onclick: pickAtRandom,
    },
    [createKeyIcon('random')],
  );

  /** The key at the foot: Start against the AI, Join with a code, Ready in a room. */
  const drawFootKey = (mine: readonly CasterId[]): void => {
    const room = roomStore.get();
    const full = mine.length === CASTERS_PER_SIDE;
    footKey.ariaPressed = null;
    if (room) {
      const ready = room.view && room.seat ? room.view.ready[room.seat] : false;
      footKey.replaceChildren(createCheckBox(ready), READY_LABEL);
      footKey.ariaPressed = String(ready);
      footKey.disabled = watching() || !room.view || !full;
      footKey.onclick = () => setRoomReady(!ready);
      return;
    }
    const now = currentOpponent();
    if (now === 'join') {
      footKey.replaceChildren(JOIN_LABEL);
      footKey.disabled = typedCode() === null;
      footKey.onclick = () => joinTypedCode(mine);
      return;
    }
    if (now === 'invite') {
      footKey.replaceChildren(createCheckBox(false), READY_LABEL);
      footKey.disabled = true;
      footKey.onclick = null;
      return;
    }
    footKey.replaceChildren(START_LABEL);
    footKey.disabled = !full;
    footKey.onclick = () => pickCasters(picked);
  };

  const draw = (): void => {
    const { mine, theirs, theirFocus } = picksNow();
    const full = mine.length === CASTERS_PER_SIDE;
    const watch = watching();
    for (const button of buttons) {
      const { id } = button.caster;
      const turn = mine.indexOf(id);
      const theirTurn = theirs.indexOf(id);
      button.root.ariaPressed = String(turn >= 0);
      button.root.disabled = watch;
      button.root.classList.toggle(TOLD_CLASS, id === told?.id);
      button.root.classList.toggle(OUT_CLASS, full && turn < 0 && theirTurn < 0);
      button.root.classList.toggle(THEIRS_CLASS, theirTurn >= 0);
      button.root.classList.toggle(THEIR_FOCUS_CLASS, id === theirFocus);
      // Every caster has the circle, empty until it is picked. A spectator picks nothing, so it has none.
      button.flag.hidden = watch;
      button.flag.classList.toggle(MINE_FLAG_CLASS, turn >= 0);
      button.flag.classList.toggle(THEIR_FLAG_CLASS, turn < 0 && theirTurn >= 0);
    }
    title.replaceChildren(
      watch ? WATCH_TITLE : TITLE,
      ...(watch
        ? []
        : [
            ' ',
            el('span', { className: 'caster-select__count', textContent: countLabel(mine.length) }),
          ]),
    );
    randomKey.disabled = watch;
    drawFootKey(mine);
    if (!told) return;
    const { id } = told;
    pane.draw({
      caster: told,
      picked: mine.includes(id),
      // A caster the other seat has is no pick.
      full: full || theirs.includes(id),
      onToggle: watch ? null : () => toggle(id),
    });
  };

  // The sheet takes the focus when the dialog opens. With none, the browser rings the first key.
  const page = el('section', { className: 'caster-select', tabIndex: -1, autofocus: true }, [
    opponent,
    el('header', { className: 'caster-select__head' }, [title, randomKey]),
    el(
      'div',
      { className: 'caster-select__list' },
      buttons.map((button) => button.root),
    ),
    pane.root,
    el('footer', { className: 'caster-select__foot' }, [footKey]),
  ]);
  const closeButton = createCloseButton(closeCasterSelect);
  const root = el('dialog', { className: 'modal caster-select-modal' }, [page, closeButton]);

  const shouldOpen = isSettingUp;
  /** With no match to go back to, the select has no cross and does not close. */
  const isFixed = (): boolean => isMatchWaiting() || roomStore.get() !== null;

  // The dialog element is the whole screen. A click that lands on it, and not on the page, is outside.
  root.addEventListener('click', (event) => {
    if (event.target === root && !isFixed()) closeCasterSelect();
  });
  // Escape closes a dialog by itself. The store has to hear of it.
  root.addEventListener('cancel', (event) => {
    if (isFixed()) event.preventDefault();
  });
  root.addEventListener('close', () => {
    if (shouldOpen() && isFixed()) root.showModal();
    else if (!roomStore.get()) closeCasterSelect();
  });

  let wasOpen = false;
  const show = (): void => {
    const open = shouldOpen();
    if (open && !wasOpen && !roomStore.get()) {
      // Each time the select opens for a match against the AI, no caster is picked, and the pane tells the first one.
      picked = [];
      told = CASTERS[0];
    }
    // A room that starts its match closes the select. The select of the AI does not come back with it.
    if (!open && wasOpen && roomStore.get()) casterSelectStore.set(false);
    wasOpen = open;
    draw();
    closeButton.hidden = isFixed();
    root.classList.toggle(ENTRY_CLASS, isFixed());
    if (!root.isConnected) return;
    // The select shows once the cover is closed over the board, and goes before the cover opens.
    const visible = open && boardCoverStore.get() === 'closed';
    if (visible && !root.open) root.showModal();
    else if (!visible && root.open) root.close();
  };
  casterSelectStore.subscribe(show);
  roomStore.subscribe(show);
  boardCoverStore.subscribe(show);
  matchWaitStore.subscribe(show);
  opponentStore.subscribe(draw);
  // The first match of Godsmode opens the select before this dialog exists. A dialog
  // outside the page cannot open, so the first draw waits until the owner put it there.
  queueMicrotask(show);
  return root;
};
